import { useEffect, useRef, useCallback, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import type { KioskPushPayload } from '@features/kiosk/types/kioskPush';

/**
 * Returns the runtime env value if it is a real URL, or undefined if it is an
 * unresolved envsubst placeholder (e.g. "${SIGNALR_HUB_URL}") or empty.
 *
 * In Docker production, `public/env-config.js` is rewritten by `docker-entrypoint.sh`
 * via envsubst before nginx serves it. In Vite dev, that template is served verbatim,
 * so `window.__ENV__.X_HUB_URL` ends up as the literal string "${X_HUB_URL}". That
 * string is truthy and would short-circuit any `|| fallback` chain, leading to a
 * SignalR negotiate request against `/${X_HUB_URL}/negotiate` -> 404.
 */
export function resolveRuntimeHubUrl(value: string | undefined): string | undefined {
  if (!value) return undefined;
  if (value.startsWith('${') && value.endsWith('}')) return undefined;
  return value;
}

interface UseSignalROptions {
  hubUrl: string;
  /** When false, no connection is opened (and an open one is closed). Defaults to true. */
  enabled?: boolean;
  onConnected?: () => void;
  onDisconnected?: (error?: Error) => void;
  onReconnecting?: (error?: Error) => void;
  onReconnected?: (connectionId?: string) => void;
}

export function useSignalR({ hubUrl, enabled = true, onConnected, onDisconnected, onReconnecting, onReconnected }: UseSignalROptions) {
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const [connectionState, setConnectionState] = useState<signalR.HubConnectionState>(
    signalR.HubConnectionState.Disconnected
  );
  const [connectionError, setConnectionError] = useState<Error | null>(null);

  // Use refs for callbacks to avoid recreating connection on callback changes
  const onConnectedRef = useRef(onConnected);
  const onDisconnectedRef = useRef(onDisconnected);
  const onReconnectingRef = useRef(onReconnecting);
  const onReconnectedRef = useRef(onReconnected);

  // Update refs when callbacks change
  useEffect(() => {
    onConnectedRef.current = onConnected;
    onDisconnectedRef.current = onDisconnected;
    onReconnectingRef.current = onReconnecting;
    onReconnectedRef.current = onReconnected;
  }, [onConnected, onDisconnected, onReconnecting, onReconnected]);

  useEffect(() => {
    // Don't connect if no hubUrl or the connection is not wanted right now
    if (!hubUrl || !enabled) return;

    // Track if effect is still active (handles React Strict Mode double-mount)
    let isMounted = true;
    let connection: signalR.HubConnection | null = null;
    let startTimeout: ReturnType<typeof setTimeout> | null = null;

    // Delay connection start slightly to handle React StrictMode double-mount
    // StrictMode unmounts and remounts components synchronously, so a small delay
    // ensures only the final mount actually starts the connection
    startTimeout = setTimeout(() => {
      if (!isMounted) return;

      connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          // Use cookies for auth instead of accessTokenFactory
          withCredentials: true,
        })
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.Warning) // Reduce noise in console
        .build();

      connection.onclose((error) => {
        if (isMounted) {
          setConnectionState(signalR.HubConnectionState.Disconnected);
          if (error) setConnectionError(error);
          onDisconnectedRef.current?.(error);
        }
      });

      connection.onreconnecting((error) => {
        if (isMounted) {
          setConnectionState(signalR.HubConnectionState.Reconnecting);
          onReconnectingRef.current?.(error);
        }
      });

      connection.onreconnected((connectionId) => {
        if (isMounted) {
          setConnectionState(signalR.HubConnectionState.Connected);
          onReconnectedRef.current?.(connectionId);
        }
      });

      connectionRef.current = connection;

      // Start connection
      connection.start()
        .then(() => {
          if (isMounted) {
            setConnectionState(signalR.HubConnectionState.Connected);
            onConnectedRef.current?.();
          }
        })
        .catch((err) => {
          // Only log if still mounted and not an abort error (from cleanup)
          if (isMounted && err?.name !== 'AbortError') {
            console.error('SignalR connection error:', err);
            setConnectionState(signalR.HubConnectionState.Disconnected);
            setConnectionError(err instanceof Error ? err : new Error(String(err)));
          }
        });
    }, 100); // 100ms delay to skip StrictMode's first mount cycle

    return () => {
      isMounted = false;
      if (startTimeout) {
        clearTimeout(startTimeout);
      }
      if (connection) {
        connection.stop().catch(() => {
          // Ignore errors when stopping
        });
      }
      connectionRef.current = null;
      // The connection is gone (unmount, disabled or new hubUrl): do not keep reporting a stale state.
      setConnectionState(signalR.HubConnectionState.Disconnected);
      setConnectionError(null);
    };
  }, [hubUrl, enabled]); // Only reconnect when hubUrl or enabled changes

  const invoke = useCallback(async <T>(methodName: string, ...args: unknown[]): Promise<T> => {
    if (!connectionRef.current || connectionRef.current.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR not connected');
    }
    return connectionRef.current.invoke<T>(methodName, ...args);
  }, []);

  const on = useCallback((methodName: string, callback: (...args: unknown[]) => void) => {
    connectionRef.current?.on(methodName, callback);
    return () => {
      connectionRef.current?.off(methodName, callback);
    };
  }, []);

  const off = useCallback((methodName: string, callback?: (...args: unknown[]) => void) => {
    if (callback) {
      connectionRef.current?.off(methodName, callback);
    } else {
      connectionRef.current?.off(methodName);
    }
  }, []);

  return {
    connection: connectionRef.current,
    connectionState,
    /** Last connect/close error of the current connection attempt; null while healthy or disabled. */
    connectionError,
    isConnected: connectionState === signalR.HubConnectionState.Connected,
    invoke,
    on,
    off,
  };
}

// Parameters received when starting safety training on a kiosk
export interface StartSafetyTrainingParams {
  safetyTrainingId: string;
  personId: string;
  identityId: string;
  personSurname: string;
  personGivenName: string;
  traceId: string;
  userId: string;
}

// Specialized hook for Kiosk Hub (connects to BFF hub directly)
export function useKioskHub(kioskId: string) {
  const hubUrl = resolveRuntimeHubUrl(window.__ENV__?.KIOSK_HUB_URL)
    || import.meta.env.VITE_KIOSK_HUB_URL
    || '/bff/hubs/kiosk';

  const { invoke, on, isConnected, connectionState } = useSignalR({
    hubUrl,
    onConnected: () => {
      console.log('Connected to Kiosk Hub');
    },
  });

  // Register kiosk when connected
  useEffect(() => {
    if (isConnected && kioskId) {
      invoke('RegisterKiosk', kioskId).catch(console.error);
    }
  }, [isConnected, kioskId, invoke]);

  const onSafetyTrainingDone = useCallback(
    (callback: () => void) => {
      return on('SafetyTrainingDone', callback);
    },
    [on]
  );

  const notifySafetyTrainingDone = useCallback(async () => {
    await invoke('SafetyTrainingDone', kioskId);
  }, [invoke, kioskId]);

  /**
   * Listen for startSafetyTraining messages from reception.
   * When received, the callback is called with the training parameters.
   */
  const onStartSafetyTraining = useCallback(
    (callback: (params: StartSafetyTrainingParams) => void) => {
      return on('startSafetyTraining', (
        safetyTrainingId: unknown,
        personId: unknown,
        identityId: unknown,
        personSurname: unknown,
        personGivenName: unknown,
        traceId: unknown,
        userId: unknown
      ) => {
        callback({
          safetyTrainingId: String(safetyTrainingId),
          personId: String(personId),
          identityId: String(identityId),
          personSurname: String(personSurname),
          personGivenName: String(personGivenName),
          traceId: String(traceId),
          userId: String(userId),
        });
      });
    },
    [on]
  );

  /**
   * Phase 34 plan 34-01: listen for `workflowPush` messages relayed by the BFF's internal kiosk
   * push endpoint (`KioskPushEndpoints`) on behalf of workflow-api. Copies
   * `onStartSafetyTraining`'s exact useCallback/on(...) shape, but the payload is a single object,
   * not positional arguments.
   */
  const onWorkflowPush = useCallback(
    (callback: (payload: KioskPushPayload) => void) => {
      return on('workflowPush', (payload: unknown) => callback(payload as KioskPushPayload));
    },
    [on]
  );

  return {
    isConnected,
    connectionState,
    onSafetyTrainingDone,
    notifySafetyTrainingDone,
    onStartSafetyTraining,
    onWorkflowPush,
  };
}

// Note: Reception dashboard now uses HTTP endpoint (/api/kiosk/start-safety-training)
// instead of client-side SignalR. See startSafetyTrainingOnKiosk in @api/endpoints/checkin.ts

// Specialized hook for Card Reader Hub
export function useCardReader(options: {
  deskReaderBase64: string | null;
  onCardRead: (cardNumber: string) => void;
  enabled?: boolean;
  /**
   * Open the hub connection only while `enabled` is true (default false: connect as soon as mounted,
   * as before). Use for pages where scanning is an occasional, user-started action.
   */
  lazyConnect?: boolean;
}) {
  const { deskReaderBase64, onCardRead, enabled = true, lazyConnect = false } = options;
  const hubUrl = resolveRuntimeHubUrl(window.__ENV__?.CARD_READER_HUB_URL)
    || import.meta.env.VITE_CARD_READER_HUB_URL
    || '/bff/hubs/iprotect';
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Counts start failures, so a repeated failure with the same message is still a new event.
  const [errorSeq, setErrorSeq] = useState(0);

  const { invoke, on, isConnected, connectionState, connectionError } = useSignalR({
    hubUrl,
    enabled: lazyConnect ? enabled : true,
    onConnected: () => {
      console.log('Connected to Card Reader Hub');
    },
    onDisconnected: (err) => {
      console.log('Disconnected from Card Reader Hub', err);
      setScanning(false);
    },
  });

  // Start listening when connected and enabled
  useEffect(() => {
    if (!isConnected || !enabled || !deskReaderBase64) {
      return;
    }

    // Start listening on the device
    const startListening = async () => {
      try {
        setScanning(true);
        setError(null);
        await invoke('listenOnDevice', deskReaderBase64);
      } catch (err) {
        console.error('Failed to start card reader:', err);
        setError(err instanceof Error ? err.message : 'Failed to start card reader');
        setErrorSeq((seq) => seq + 1);
        setScanning(false);
      }
    };

    startListening();

    // Listen for card read results
    const unsubscribe = on('cardReaderResult', (cardNumber: unknown) => {
      if (typeof cardNumber === 'string' && cardNumber.trim()) {
        onCardRead(cardNumber);
      }
    });

    return () => {
      unsubscribe();
      setScanning(false);
    };
  }, [isConnected, enabled, deskReaderBase64, invoke, on, onCardRead]);

  return {
    isConnected,
    connectionState,
    /** Why the hub connection could not be established (null if fine / not attempted). */
    connectionError,
    scanning,
    error,
    errorSeq,
  };
}
